import React, { useEffect, useRef } from 'react';

type Node = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  hub: boolean;
  reveal: number;
};

const NODE_COUNT = 44;
const HUB_COUNT = 6;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

const placePoint = (
  existing: Array<{ x: number; y: number }>,
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minDistance = 0.07,
) => {
  for (let attempt = 0; attempt < 24; attempt += 1) {
    const x = randomBetween(minX, maxX);
    const y = randomBetween(minY, maxY);
    const tooClose = existing.some((point) => Math.hypot(point.x - x, point.y - y) < minDistance);
    if (!tooClose) return { x, y };
  }

  return { x: randomBetween(minX, maxX), y: randomBetween(minY, maxY) };
};

const createNodes = (): Node[] => {
  const nodes: Node[] = [];

  for (let index = 0; index < HUB_COUNT; index += 1) {
    const point = placePoint(nodes, 0.22, 0.78, 0.22, 0.74, 0.12);
    nodes.push({
      ...point,
      vx: (Math.random() - 0.5) * 0.00018,
      vy: (Math.random() - 0.5) * 0.00018,
      hub: true,
      reveal: index < 3 ? 0 : randomBetween(0.06, 0.28),
    });
  }

  for (let index = nodes.length; index < NODE_COUNT; index += 1) {
    const isCore = index < 18;
    const point = isCore
      ? placePoint(nodes, 0.2, 0.8, 0.18, 0.78, 0.06)
      : placePoint(nodes, 0.04, 0.96, 0.06, 0.94, 0.05);

    nodes.push({
      ...point,
      vx: (Math.random() - 0.5) * 0.00022,
      vy: (Math.random() - 0.5) * 0.00022,
      hub: false,
      reveal: isCore ? 0 : randomBetween(0.1, 0.92),
    });
  }

  return nodes;
};

const easeOutCubic = (value: number) => 1 - (1 - value) ** 3;

const HeroNetwork: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nodes = createNodes();
    const hero = canvas.closest('.consulting-hero');

    let frame = 0;
    let width = 0;
    let height = 0;
    let progress = reduceMotion ? 0.55 : 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const updateProgress = () => {
      if (!hero || reduceMotion) return;
      const rect = hero.getBoundingClientRect();
      const travel = Math.max(rect.height * 0.7, window.innerHeight * 0.55);
      progress = Math.min(1, Math.max(0, -rect.top / travel));
    };

    const visibility = (node: Node, eased: number) => {
      if (node.reveal <= 0) return 1;
      return Math.min(1, Math.max(0, (eased - node.reveal) / 0.16));
    };

    const draw = () => {
      const eased = easeOutCubic(progress);
      const zoom = 1.16 - eased * 0.46;
      const maxDistance = 0.22 + eased * 0.18;

      if (!reduceMotion) {
        nodes.forEach((node) => {
          node.x += node.vx;
          node.y += node.vy;
          if (node.x < 0.03 || node.x > 0.97) node.vx *= -1;
          if (node.y < 0.04 || node.y > 0.96) node.vy *= -1;
        });
      }

      context.clearRect(0, 0, width, height);
      context.save();
      context.translate(width / 2, height / 2);
      context.scale(zoom, zoom);
      context.translate(-width / 2, -height / 2);

      for (let i = 0; i < nodes.length; i += 1) {
        const from = nodes[i];
        const fromVisible = visibility(from, eased);
        if (fromVisible <= 0) continue;

        for (let j = i + 1; j < nodes.length; j += 1) {
          const to = nodes[j];
          const toVisible = visibility(to, eased);
          if (toVisible <= 0) continue;

          const dx = from.x - to.x;
          const dy = from.y - to.y;
          const distance = Math.hypot(dx, dy);
          const reach = from.hub || to.hub ? maxDistance * 1.28 : maxDistance;
          if (distance > reach) continue;

          const edgeReveal = Math.max(from.reveal, to.reveal);
          const grow = Math.min(1, Math.max(0, (eased - edgeReveal + 0.2) / 0.2));
          if (grow <= 0) continue;

          const strength = (1 - distance / reach) * fromVisible * toVisible * grow;
          const x1 = from.x * width;
          const y1 = from.y * height;
          const x2 = x1 + (to.x * width - x1) * grow;
          const y2 = y1 + (to.y * height - y1) * grow;

          context.beginPath();
          context.moveTo(x1, y1);
          context.lineTo(x2, y2);
          context.strokeStyle = `rgba(186, 230, 255, ${0.14 + strength * 0.5})`;
          context.lineWidth = from.hub || to.hub ? 1.5 : 1.1;
          context.stroke();
        }
      }

      const hubs = nodes.filter((node) => node.hub);
      nodes.forEach((node) => {
        const nodeVisible = visibility(node, eased);
        if (nodeVisible <= 0 || node.hub) return;

        let nearest = hubs[0];
        let nearestDistance = Infinity;
        hubs.forEach((hub) => {
          if (visibility(hub, eased) <= 0) return;
          const distance = Math.hypot(node.x - hub.x, node.y - hub.y);
          if (distance < nearestDistance) {
            nearest = hub;
            nearestDistance = distance;
          }
        });

        if (!nearest || nearestDistance > 0.4) return;
        const grow = Math.min(1, Math.max(0, (eased - Math.max(node.reveal, nearest.reveal) + 0.18) / 0.18));
        if (grow <= 0) return;

        const x1 = node.x * width;
        const y1 = node.y * height;
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x1 + (nearest.x * width - x1) * grow, y1 + (nearest.y * height - y1) * grow);
        context.strokeStyle = `rgba(186, 230, 255, ${0.16 + nodeVisible * grow * 0.28})`;
        context.lineWidth = 1.15;
        context.stroke();
      });

      nodes.forEach((node) => {
        const appear = visibility(node, eased);
        if (appear <= 0) return;

        const x = node.x * width;
        const y = node.y * height;
        const radius = node.hub ? 3.6 : 2.2;

        const glow = context.createRadialGradient(x, y, 0, x, y, radius * 7);
        glow.addColorStop(0, `rgba(125, 211, 252, ${0.28 * appear})`);
        glow.addColorStop(1, 'rgba(125, 211, 252, 0)');
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, radius * 7, 0, Math.PI * 2);
        context.fill();

        context.beginPath();
        context.arc(x, y, radius, 0, Math.PI * 2);
        context.fillStyle = `rgba(255, 255, 255, ${0.55 + appear * 0.4})`;
        context.fill();
      });

      context.restore();
      frame = window.requestAnimationFrame(draw);
    };

    resize();
    updateProgress();
    frame = window.requestAnimationFrame(draw);

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return <canvas className="hero-network" ref={canvasRef} />;
};

export default HeroNetwork;
